import React, { useState } from 'react';
import { format } from 'date-fns';
import api from '../utils/axiosConfig';

const SubmissionsDisplay = ({ submissions, onStatusChange, refreshSubmissions, currentEvent }) => {
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [printingSubmissionId, setPrintingSubmissionId] = useState(null);
  
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };
  
  // Get status badge with appropriate color
  const getStatusBadge = (status) => {
    const statusMap = {
      'APPROVED': 'approved',
      'PENDING': 'pending',
      'REJECTED': 'rejected'
    };

    const mappedStatus = statusMap[status] || status?.toLowerCase() || 'unknown';
    
    const statusConfig = {
      approved: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' }
    };
    
    const config = statusConfig[mappedStatus] || { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config.bg} ${config.text} ${config.border}`}>
        {status || 'Unknown'}
      </span>
    );
  };
  
  const getRsvpIndicator = (rsvp) => {
    if (rsvp === 'ATTENDING') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 border border-blue-200">Attending</span>;
    } else if (rsvp === 'NOT_ATTENDING') {
      return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-200">Not Attending</span>;
    }
    return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-500 border border-gray-200">No Response</span>;
  };
  
  const openSubmissionDetails = (submission) => {
    setSelectedSubmission(submission);
    setShowModal(true);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedSubmission(null), 300); // Clear data after animation
  };

  const handleStatusChange = async (submissionId, newStatus) => {
    if (!submissionId || !newStatus || actionLoading) return;
    
    setActionLoading(true);
    try {
      await api.put(`/events/${currentEvent.id}/submissions/${submissionId}/`, {
        status: newStatus
      });
      
      // Call the parent component's function to refresh the data
      if (refreshSubmissions) {
        await refreshSubmissions();
      }
      
      // If we're updating the currently selected submission, update its status in the state
      if (selectedSubmission && selectedSubmission.id === submissionId) {
        setSelectedSubmission({
          ...selectedSubmission,
          status: newStatus
        });
      }
    } catch (error) {
      console.error('Error updating submission status:', error);
      alert('Failed to update submission status. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle print badge
  const handlePrintBadge = async (submissionId) => {
    if (!submissionId || printingSubmissionId) return;
    
    setPrintingSubmissionId(submissionId);
    try {
      // Call the API endpoint that will return a PDF or image badge
      const response = await api.get(`/events/${currentEvent.id}/submissions/${submissionId}/badge/`, {
        responseType: 'blob' // Important: This tells axios to treat the response as binary data
      });
      
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/pdf' 
      });
      const url = window.URL.createObjectURL(blob);
      
      window.open(url, '_blank');
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 30000);
      
    } catch (error) {
      console.error('Error printing badge:', error);
      alert('Failed to print badge. Please try again.');
    } finally {
      setPrintingSubmissionId(null);
    }
  };

  const renderModalContent = () => {
    if (!selectedSubmission) return null;
    
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">
            Submission Details: {selectedSubmission.first_name} {selectedSubmission.last_name}
          </h3>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Basic Info */}
        <div className="mb-8">
          <h4 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h4>
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="text-sm font-medium text-gray-900">
                  {selectedSubmission.first_name} {selectedSubmission.last_name}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{selectedSubmission.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">{selectedSubmission.phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Ticket Type</p>
                <p className="text-sm font-medium text-gray-900">{selectedSubmission.ticket_type || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <div className="mt-1">{getStatusBadge(selectedSubmission.status)}</div>
              </div>
              <div>
                <p className="text-sm text-gray-500">RSVP</p>
                <div className="mt-1">{getRsvpIndicator(selectedSubmission.rsvp)}</div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Submitted On</p>
                <p className="text-sm font-medium text-gray-900">
                  {formatDate(selectedSubmission.submitted_at)}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions Section */}
        <div className="mb-8">
          <h4 className="text-lg font-medium text-gray-900 mb-3">Actions</h4>
          <div className="flex flex-wrap gap-2">
            {/* Status actions */}
            {!currentEvent?.auto_approve && (
              <>
                <button
                  onClick={() => handleStatusChange(selectedSubmission.id, 'APPROVED')}
                  disabled={selectedSubmission.status === 'APPROVED' || actionLoading}
                  className={`px-3 py-1 text-sm rounded border ${
                    selectedSubmission.status === 'APPROVED' 
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' 
                      : 'bg-green-50 text-green-700 hover:bg-green-100 border-green-200'
                  }`}
                >
                  Approve
                </button>
                <button
                  onClick={() => handleStatusChange(selectedSubmission.id, 'REJECTED')}
                  disabled={selectedSubmission.status === 'REJECTED' || actionLoading}
                  className={`px-3 py-1 text-sm rounded border ${
                    selectedSubmission.status === 'REJECTED' 
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' 
                      : 'bg-red-50 text-red-700 hover:bg-red-100 border-red-200'
                  }`}
                >
                  Reject
                </button>
                <button
                  onClick={() => handleStatusChange(selectedSubmission.id, 'PENDING_APPROVAL')}
                  disabled={selectedSubmission.status === 'PENDING_APPROVAL' || actionLoading}
                  className={`px-3 py-1 text-sm rounded border ${
                    selectedSubmission.status === 'PENDING_APPROVAL' 
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' 
                      : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-200'
                  }`}
                >
                  Mark Pending
                </button>
              </>
            )}
            
            {/* Print Badge button - always available */}
            <button
              onClick={() => handlePrintBadge(selectedSubmission.id)}
              disabled={printingSubmissionId === selectedSubmission.id}
              className="px-3 py-1 text-sm bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded border border-indigo-200 flex items-center"
            >
              {printingSubmissionId === selectedSubmission.id ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-indigo-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print Badge
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Additional Information */}
        <div className="mb-8">
          <h4 className="text-lg font-medium text-gray-900 mb-3">Additional Information</h4>
          {selectedSubmission.data && Object.keys(selectedSubmission.data).length > 0 ? (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(selectedSubmission.data).map(([key, value]) => (
                  <div key={key}>
                    <p className="text-sm text-gray-500">
                      {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {typeof value === 'boolean' 
                        ? value ? 'Yes' : 'No'
                        : Array.isArray(value)
                          ? value.join(', ')
                          : typeof value === 'object' && value !== null
                            ? JSON.stringify(value)
                            : value?.toString() || 'N/A'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 italic">No additional data</p>
          )}
        </div>
        
        {/* Uploaded Files */}
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-3">Uploaded Files</h4>
          {selectedSubmission.files && selectedSubmission.files.length > 0 ? (
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 gap-2">
                {selectedSubmission.files.map((file, index) => (
                  <div key={file.id} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name || `File ${index + 1}`}</p>
                    </div>
                    <a 
                      href={file.file} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="px-3 py-1 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded border border-blue-200"
                    >
                      View File
                    </a>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 italic">No files uploaded</p>
          )}
        </div>
      </div>
    );
  };
  
  if (!submissions || submissions.length === 0) {
    return <div className="bg-white shadow rounded-lg p-6 text-center">No submissions found</div>;
  }
  
  return (
    <>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl font-bold text-gray-900">Event Submissions</h2>
          <p className="text-sm text-gray-600 mt-1">{submissions.length} submissions total</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RSVP</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {submissions.map((submission) => (
                <tr key={submission.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {submission.first_name} {submission.last_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {submission.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {submission.phone || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {submission.ticket_type || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(submission.submitted_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(submission.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getRsvpIndicator(submission.rsvp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex space-x-4">
                      <button
                        onClick={() => openSubmissionDetails(submission)}
                        className="text-indigo-600 hover:text-indigo-900 focus:outline-none"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handlePrintBadge(submission.id)}
                        className="text-emerald-600 hover:text-emerald-900 focus:outline-none flex items-center"
                        disabled={printingSubmissionId === submission.id}
                      >
                        {printingSubmissionId === submission.id ? (
                          <>
                            <svg className="animate-spin h-4 w-4 mr-1 text-emerald-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            Badge
                          </>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {showModal && (
        <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={closeModal}></div>
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            {renderModalContent()}
          </div>
        </div>
      )}
    </>
  );
};

export default SubmissionsDisplay;